/**
 * Projects Table Component
 *
 * Displays projects in a responsive table with sorting, pagination,
 * and clickable rows that navigate to project detail.
 *
 * @module components/projects/ProjectsTable
 */

import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Pagination, Spinner, Alert, Card, Dropdown, Row, Col, Modal, Button, ProgressBar, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import ProjectStatusBadge from '../common/ProjectStatusBadge';
import { formatCurrency, formatDate, formatPercentage } from '../../utils/formatters';
import { deleteProject, calculateBudgetMetrics } from '../../services/projectService';
import { useAuth } from '../../contexts/AuthContext';
import { getBudgetColorVariant, getBudgetStatusLabel } from '../../utils/budgetStatus';
import { toast } from '../../utils/toast';
import useViewToggle from '../../hooks/useViewToggle';
import ViewToggle from '../common/ViewToggle';
import { DataTable } from '../common/DataTable';
import KebabMenu from '../common/KebabMenu';
import './ProjectsTable.css';

/**
 * ProjectsTable Component
 *
 * @param {Object} props - Component props
 * @param {Array} props.projects - Array of projects to display
 * @param {boolean} props.isLoading - Loading state
 * @param {Error} props.error - Error object if any
 * @param {Function} [props.onRetry] - Refetch callback for the error state
 * @param {Object} props.filters - Current filter values (for pagination)
 * @param {Function} props.onFilterChange - Callback to change page
 * @param {number} props.totalCount - Total number of projects (for pagination)
 */
const ProjectsTable = ({
  projects = [],
  isLoading = false,
  error = null,
  onRetry = null,
  filters,
  onFilterChange,
  totalCount = 0,
  budgetTotals = {},
  isBudgetLoading = false,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { viewMode, setViewMode, isTableView } = useViewToggle();
  const [projectToDelete, setProjectToDelete] = useState(null);
  // Solo el admin tiene política DELETE sobre `projects`. Ofrecerle "Eliminar" a
  // un supervisor/técnico es ofrecer una acción que la base va a ignorar en
  // silencio: no se esconde por estética, se esconde porque no existe para él.
  const { isAdmin } = useAuth();

  // Merge each project's expense/committed totals (fetched in a single
  // batched call by the parent, not per-row -- #406) with the same
  // percentage/status math used by the project detail budget card and the
  // dashboard "Resumen de Presupuestos" widget, so all three agree (#276,
  // #325, #406). Unlike the dashboard widget, every project on the page is
  // shown here -- this is an audit table, not a top-5 executive summary.
  const projectsWithBudget = useMemo(
    () =>
      projects.map((project) => {
        const totals = budgetTotals[project.id] || { totalExpenses: 0, committedAmount: 0 };
        const metrics = calculateBudgetMetrics(
          project.quoted_amount || 0,
          totals.totalExpenses,
          totals.committedAmount
        );
        return {
          ...project,
          totalExpenses: totals.totalExpenses,
          committedAmount: totals.committedAmount,
          percentageUsed: metrics.percentageUsed,
        };
      }),
    [projects, budgetTotals]
  );

  // `deleteProject` devuelve `{success, error}` en vez de tirar. Con
  // `mutationFn: deleteProject` a secas, react-query consideraba éxito CUALQUIER
  // resolución — incluido `{success:false}` — y disparaba el toast verde sobre
  // un proyecto que seguía existiendo. Hay que convertir el fallo en throw para
  // que `onError` sea alcanzable.
  const deleteMutation = useMutation({
    mutationFn: async (projectId) => {
      const { success, error } = await deleteProject(projectId);
      if (!success) {
        throw error || new Error('No se pudo eliminar el proyecto');
      }
      return projectId;
    },
    onSuccess: () => {
      toast.success('Proyecto eliminado exitosamente');
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      setProjectToDelete(null);
    },
    onError: (err) => {
      toast.error('Error al eliminar proyecto: ' + (err?.message || 'desconocido'));
    },
  });

  const confirmDelete = () => {
    if (projectToDelete) {
      deleteMutation.mutate(projectToDelete.id);
    }
  };

  // Calculate pagination
  const currentPage = filters.page || 0;
  const pageSize = filters.pageSize || 20;
  const totalPages = Math.ceil(totalCount / pageSize);

  // Handle row click
  const handleRowClick = (projectId) => {
    navigate(`/proyectos/${projectId}`);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    onFilterChange('page', newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // DataTable column definitions for the table view
  const projectColumns = useMemo(
    () => [
      {
        id: 'title',
        header: 'Nombre',
        accessor: 'title',
        minWidth: 200,
        cell: (project) => (
          <div className="fw-semibold text-truncate" style={{ maxWidth: '300px' }}>
            {project.title || project.project_number}
          </div>
        ),
      },
      {
        id: 'project_number',
        header: 'ID',
        accessor: 'project_number',
        minWidth: 110,
        cell: (project) => (
          <span className="small text-muted font-monospace" title="ID interno del proyecto (generado automáticamente)">
            {project.project_number}
          </span>
        ),
      },
      {
        id: 'client',
        header: 'Cliente',
        accessor: (p) =>
          p.client?.company_name || p.client?.primary_contact_name || '-',
        minWidth: 150,
      },
      {
        id: 'status',
        header: 'Estado',
        accessor: 'status',
        align: 'center',
        minWidth: 110,
        cell: (project) => <ProjectStatusBadge status={project.status} size="sm" />,
      },
      {
        id: 'created_at',
        header: 'Fecha',
        accessor: 'created_at',
        minWidth: 110,
        cell: (project) => (
          <span className="text-muted">{formatDate(project.created_at)}</span>
        ),
      },
      {
        id: 'quoted_amount',
        header: 'Monto',
        accessor: (p) => p.quoted_amount || 0,
        align: 'right',
        minWidth: 110,
        cell: (project) =>
          project.quoted_amount ? (
            <strong>{formatCurrency(project.quoted_amount)}</strong>
          ) : (
            <span className="text-muted">-</span>
          ),
      },
      {
        id: 'spent',
        header: 'Gastado',
        accessor: (p) => p.totalExpenses || 0,
        align: 'right',
        minWidth: 120,
        cell: (project) => {
          if (isBudgetLoading) {
            return <Spinner animation="border" size="sm" className="text-muted" />;
          }
          const variant = getBudgetColorVariant(project.percentageUsed);
          return (
            <span className={`text-${variant} fw-semibold`} style={{ fontSize: '0.9rem' }}>
              {formatCurrency(project.totalExpenses)}
            </span>
          );
        },
      },
      {
        id: 'budget_usage',
        header: 'Uso',
        accessor: (p) => p.percentageUsed || 0,
        minWidth: 160,
        cell: (project) => {
          if (isBudgetLoading) {
            return <Spinner animation="border" size="sm" className="text-muted" />;
          }
          const variant = getBudgetColorVariant(project.percentageUsed);
          return (
            <>
              <div className="mb-2">
                <span
                  className={`fw-bold text-${variant}`}
                  style={{ fontSize: '0.95rem' }}
                  data-testid={`projects-table-budget-percentage-${project.id}`}
                >
                  {formatPercentage(project.percentageUsed, 1)}
                </span>
              </div>
              <ProgressBar
                now={Math.min(project.percentageUsed, 100)}
                variant={variant}
                style={{ height: '8px', borderRadius: '4px' }}
              />
            </>
          );
        },
      },
      {
        id: 'budget_status',
        header: 'Estado Presupuesto',
        accessor: (p) => p.percentageUsed || 0,
        align: 'center',
        minWidth: 130,
        cell: (project) => {
          if (isBudgetLoading) {
            return <Spinner animation="border" size="sm" className="text-muted" />;
          }
          const variant = getBudgetColorVariant(project.percentageUsed);
          const statusLabel = getBudgetStatusLabel(project.percentageUsed);
          return (
            <Badge
              bg={variant}
              className={`px-3 py-2 ${variant === 'warning' ? 'text-dark' : ''}`}
              style={{ fontSize: '0.8rem', fontWeight: '600' }}
            >
              {statusLabel}
            </Badge>
          );
        },
      },
      {
        id: 'po_number',
        header: 'P.O.',
        accessor: 'po_number',
        align: 'center',
        minWidth: 90,
        cell: (project) =>
          project.po_number ? (
            <span className="badge bg-info text-dark">
              <i className="bi bi-file-check me-1"></i>
              {project.po_number}
            </span>
          ) : (
            <span className="text-muted">-</span>
          ),
      },
      {
        id: 'actions',
        header: '',
        sortable: false,
        resizable: false,
        align: 'center',
        noSelect: true,
        minWidth: 50,
        maxWidth: 60,
        cell: (project) => (
          <div data-no-select onClick={(e) => e.stopPropagation()}>
            <KebabMenu label={`Acciones de ${project.title || project.project_number}`}>
              <Dropdown.Item onClick={() => navigate(`/proyectos/${project.id}`)}>
                <i className="bi bi-eye me-2"></i>Ver Detalle
              </Dropdown.Item>
              <Dropdown.Item onClick={() => navigate(`/proyectos/${project.id}/editar`)}>
                <i className="bi bi-pencil me-2"></i>Editar
              </Dropdown.Item>
              {isAdmin && (
                <>
                  <Dropdown.Divider />
                  <Dropdown.Item className="text-danger" onClick={() => setProjectToDelete(project)}>
                    <i className="bi bi-trash me-2"></i>Eliminar
                  </Dropdown.Item>
                </>
              )}
            </KebabMenu>
          </div>
        ),
      },
    ],
    [navigate, isBudgetLoading, isAdmin]
  );

  // Loading state
  if (isLoading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3 text-muted">Cargando proyectos...</p>
      </div>
    );
  }

  // Error state. Alcanzable solo si el padre desenvuelve el resultado del
  // servicio (`unwrapServiceResult`): sin eso `error` es siempre null y esto es
  // codigo muerto. Mismo patron de recuperacion que InventoryList/ExpenseList.
  if (error) {
    return (
      <Alert variant="danger">
        <Alert.Heading>Error al cargar proyectos</Alert.Heading>
        <p>{error.message || 'Ocurrió un error inesperado'}</p>
        {onRetry && (
          <Button variant="outline-danger" onClick={() => onRetry()}>
            <i className="bi bi-arrow-clockwise me-1"></i>
            Reintentar
          </Button>
        )}
      </Alert>
    );
  }

  // Empty state
  if (!projects || projects.length === 0) {
    return (
      <div className="text-center py-5">
        <i className="bi bi-inbox" style={{ fontSize: '3rem', color: '#ccc' }}></i>
        <h5 className="mt-3 text-muted">No se encontraron proyectos</h5>
        <p className="text-muted">
          {filters.search || filters.status || filters.client_id
            ? 'Intenta ajustar los filtros para ver más resultados'
            : 'Crea tu primer proyecto usando el tab "Nuevo Proyecto"'}
        </p>
      </div>
    );
  }

  return (
    <>
      {/* View Toggle */}
      <div className="d-flex justify-content-end mb-3">
        <ViewToggle viewMode={viewMode} onToggle={setViewMode} />
      </div>

      {isTableView ? (
        <DataTable
          tableId="projects"
          data={projectsWithBudget}
          rowKey={(p) => p.id}
          onRowClick={(project) => handleRowClick(project.id)}
          columns={projectColumns}
        />
      ) : (
        <Row className="g-3">
          {projectsWithBudget.map((project) => (
            <Col xs={12} sm={6} lg={4} key={project.id}>
              <Card
                className={`project-card project-card-${project.status}`}
                onClick={() => handleRowClick(project.id)}
                tabIndex={0}
                onKeyPress={(e) => e.key === 'Enter' && handleRowClick(project.id)}
                role="button"
                aria-label={`Ver proyecto ${project.project_number}`}
              >
                <Card.Body>
                  <div className="project-card-header">
                    <span className="badge bg-light text-secondary border fw-normal font-monospace" title="ID interno del proyecto (generado automáticamente)">
                      <i className="bi bi-hash me-1"></i>{project.project_number}
                    </span>
                    <div className="project-card-header-actions">
                      <ProjectStatusBadge status={project.status} size="sm" />
                      <KebabMenu label={`Acciones de ${project.title || project.project_number}`}>
                        <Dropdown.Item onClick={() => navigate(`/proyectos/${project.id}`)}>
                          <i className="bi bi-eye me-2"></i>Ver Detalle
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => navigate(`/proyectos/${project.id}/editar`)}>
                          <i className="bi bi-pencil me-2"></i>Editar
                        </Dropdown.Item>
                        {isAdmin && (
                          <>
                            <Dropdown.Divider />
                            <Dropdown.Item className="text-danger" onClick={() => setProjectToDelete(project)}>
                              <i className="bi bi-trash me-2"></i>Eliminar
                            </Dropdown.Item>
                          </>
                        )}
                      </KebabMenu>
                    </div>
                  </div>

                  <h6 className="project-card-title mt-2">
                    {project.title || project.project_number}
                  </h6>

                  {(project.client?.company_name || project.client?.primary_contact_name) && (
                    <div className="project-card-client">
                      <i className="bi bi-building"></i>
                      {project.client?.company_name || project.client?.primary_contact_name}
                    </div>
                  )}

                  {/* Budget usage: same progress bar + badge pattern as the
                      dashboard "Resumen de Presupuestos" widget's mobile
                      card, so both read the same way on a phone (#406). */}
                  {isBudgetLoading ? (
                    <div className="mb-3 text-muted small">
                      <Spinner animation="border" size="sm" className="me-2" />
                      Cargando presupuesto...
                    </div>
                  ) : (
                    <div className="mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span className="text-muted" style={{ fontSize: '0.75rem' }}>Uso del presupuesto</span>
                        <span
                          className={`fw-bold text-${getBudgetColorVariant(project.percentageUsed)}`}
                          style={{ fontSize: '0.85rem' }}
                          data-testid={`projects-table-budget-percentage-mobile-${project.id}`}
                        >
                          {formatPercentage(project.percentageUsed, 1)}
                        </span>
                      </div>
                      <ProgressBar
                        now={Math.min(project.percentageUsed, 100)}
                        variant={getBudgetColorVariant(project.percentageUsed)}
                        style={{ height: '8px', borderRadius: '4px' }}
                      />
                    </div>
                  )}

                  <div className="project-card-info">
                    <div className="project-card-info-item">
                      <span className="project-card-info-label">Monto</span>
                      <span className="project-card-info-value">
                        {project.quoted_amount
                          ? formatCurrency(project.quoted_amount)
                          : '-'}
                      </span>
                    </div>

                    {!isBudgetLoading && (
                      <div className="project-card-info-item">
                        <span className="project-card-info-label">Gastado</span>
                        <span className={`project-card-info-value text-${getBudgetColorVariant(project.percentageUsed)}`}>
                          {formatCurrency(project.totalExpenses)}
                        </span>
                      </div>
                    )}

                    {!isBudgetLoading && (
                      <div className="project-card-info-item">
                        <span className="project-card-info-label">Estado</span>
                        <Badge
                          bg={getBudgetColorVariant(project.percentageUsed)}
                          className={`px-2 py-1 ${getBudgetColorVariant(project.percentageUsed) === 'warning' ? 'text-dark' : ''}`}
                          style={{ fontSize: '0.7rem', fontWeight: '600' }}
                        >
                          {getBudgetStatusLabel(project.percentageUsed)}
                        </Badge>
                      </div>
                    )}

                    {project.po_number && (
                      <div className="project-card-info-item">
                        <span className="project-card-info-label">P.O.</span>
                        <span className="project-card-info-value text-info">
                          {project.po_number}
                        </span>
                      </div>
                    )}

                    <div className="project-card-info-item">
                      <span className="project-card-info-label">Fecha</span>
                      <span className="project-card-info-value">
                        {formatDate(project.created_at)}
                      </span>
                    </div>
                  </div>

                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-center mt-4">
          <Pagination>
            <Pagination.First
              onClick={() => handlePageChange(0)}
              disabled={currentPage === 0}
            />
            <Pagination.Prev
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 0}
            />

            {/* Page numbers */}
            {[...Array(totalPages)].map((_, idx) => {
              // Show current page, previous, next, and first/last pages
              if (
                idx === 0 ||
                idx === totalPages - 1 ||
                (idx >= currentPage - 1 && idx <= currentPage + 1)
              ) {
                return (
                  <Pagination.Item
                    key={idx}
                    active={idx === currentPage}
                    onClick={() => handlePageChange(idx)}
                  >
                    {idx + 1}
                  </Pagination.Item>
                );
              } else if (idx === currentPage - 2 || idx === currentPage + 2) {
                return <Pagination.Ellipsis key={idx} disabled />;
              }
              return null;
            })}

            <Pagination.Next
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= totalPages - 1}
            />
            <Pagination.Last
              onClick={() => handlePageChange(totalPages - 1)}
              disabled={currentPage >= totalPages - 1}
            />
          </Pagination>
        </div>
      )}

      {/* Results count */}
      <div className="text-center text-muted small mt-2">
        Mostrando {projects.length} de {totalCount} proyectos
        {currentPage > 0 && ` - Pagina ${currentPage + 1} de ${totalPages}`}
      </div>

      {/* Delete confirmation modal */}
      <Modal show={!!projectToDelete} onHide={() => setProjectToDelete(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Eliminar Proyecto</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Estas seguro de que deseas eliminar el proyecto{' '}
            <strong>{projectToDelete?.project_number}</strong> -{' '}
            <strong>{projectToDelete?.title}</strong>?
          </p>
          <p className="text-muted small mb-0">Esta accion no se puede deshacer.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setProjectToDelete(null)}
            disabled={deleteMutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            variant="danger"
            onClick={confirmDelete}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Eliminando...
              </>
            ) : (
              <>
                <i className="bi bi-trash me-1"></i>
                Eliminar
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

ProjectsTable.propTypes = {
  projects: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      project_number: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired,
      quoted_amount: PropTypes.number,
      po_number: PropTypes.string,
      created_at: PropTypes.string.isRequired,
      client: PropTypes.shape({
        full_name: PropTypes.string,
        company_name: PropTypes.string,
      }),
    })
  ),
  isLoading: PropTypes.bool,
  error: PropTypes.object,
  onRetry: PropTypes.func,
  filters: PropTypes.object.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  budgetTotals: PropTypes.objectOf(
    PropTypes.shape({
      totalExpenses: PropTypes.number,
      committedAmount: PropTypes.number,
    })
  ),
  isBudgetLoading: PropTypes.bool,
  totalCount: PropTypes.number,
};

export default ProjectsTable;
